import { faChalkboardTeacher, faHandshake } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApiGet } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import ClpTable from "components/ClpTable"
import { MultiSelectFilter, SelectColumnFilter } from "components/ClpTable/ClpTableFilterTypes"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import { useContext, useEffect, useState } from "react"
import Moment from "react-moment"
import { Link, useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"
import { SearchUserDialog } from "./pages/edit/components/SearchUserDialog"
import { useTranslation } from "react-i18next"
import { t } from "i18next"
import { useApi } from "auth/useApi3"
import ContinueDialog from "components/Dialogs/ContinueDialog"

export const booleanAsString = (value) => {
  switch (value) {
    case true:
      return t("global.yes", "Yes")

    default:
      return t("global.no", "No")
  }
}

const Customers = () => {
  const routerHistory = useHistory()
  const appContext = useContext(AppContext)

  const [locizeResult, locizeApi] = useApi()
  const [customersResult, getCustomers] = useApiGet()
  const loadingContext = useContext(GlobalLoaderContext)
  const [openUserDialog, setOpenUserDialog] = useState(false)
  const [showLocizeDialog, setShowLocizeDialog] = useState(false)
  const [locizeDialogMessage, setLocizeDialogMessage] = useState("")

  const theme = useTheme()
  const isScreenBig = useMediaQuery(theme.breakpoints.up("xl"))
  const { t } = useTranslation()

  useEffect(() => {
    getCustomers(apiEndpoints.systemadmincustomers.listcustomers)
  }, [getCustomers])

  useEffect(() => {
    if (showLocizeDialog) {
      setLocizeDialogMessage(t("sysadmin.update_locize_confirmation_text", "Are you sure you want to update to the latest translations from Locize? This will update the translation files for sv, en, da, and nb."))
    }
  }, [showLocizeDialog, t])

  useEffect(() => {
    loadingContext.setLoading(() => customersResult.status === 1)
  })

  const columns = [
    {
      accessorKey: "name",
      header: t("global.name", "Name")
    },
    {
      accessorKey: "organisationNumber",
      header: t("global.org_no", "Org. No.")
    },
    {
      accessorKey: "streetAddress",
      header: t("global.postal_address", "Postal address")
    },
    {
      accessorKey: "postalCode",
      header: t("global.postal_code", "ZIP code")
    },
    {
      accessorKey: "cityAddress",
      header: t("global.city", "City")
    },
    {
      accessorFn: (row) => {
        return booleanAsString(row.isDemoAccount, t)
      },
      cell: ({ cell }) => {
        return (
          cell.row.original.isDemoAccount && (
            <Box color={`primary.main`}>
              <FontAwesomeIcon icon={faChalkboardTeacher} />
              <Typography sx={{ marginLeft: "5px" }} variant="caption">
                {t("global.demo", "Demo")}
              </Typography>
            </Box>
          )
        )
      },
      header: t("global.demo_account", "Demo account"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter,
        filterHeader: t("global.demo_account", "Demo account"),
        align: isScreenBig ? "left" : "right",
        filterOptions: [
          {
            name: t("global.demo_account", "Demo account"),
            value: t("global.yes", "Yes")
          },
          {
            name: t("global.no_demo_account ", "No demo account"),
            value: t("global.no", "No")
          }
        ]
      }
    },
    {
      accessorKey: "createdDate",
      header: t("global.created_when", "Created when"),
      meta: {
        filter: MultiSelectFilter
      },

      cell: ({ cell }) => {
        return (
          cell.row.original.createdDate && (
            <Moment format="YYYY-MM-DD">{cell.row.original.createdDate}</Moment>
          )
        )
      }
    }
  ]

  if (hasRole(appContext.appUser, allRoles.SystemAdminCustomers)) {
    columns.push(
      {
        accessorFn: (row) => {
          return booleanAsString(row.isPartner, t)
        },
        cell: ({ cell }) => {
          return (
            cell.row.original.isPartner && (
              <Box color={`primary.main`}>
                <FontAwesomeIcon icon={faHandshake} />
                <Typography sx={{ marginLeft: "5px" }} variant="caption">
                  {t("global.partner", "Partner")}
                </Typography>
              </Box>
            )
          )
        },
        filterFn: "arrIncludesSome",
        header: t("global.partner", "Partner"),
        meta: {
          mainFilter: true,
          filter: MultiSelectFilter,
          filterHeader: t("global.partner", "Partner"),
          align: isScreenBig ? "left" : "right",
          filterOptions: [
            {
              name: t("global.partner", "Partner"),
              value: t("global.yes", "Yes")
            },
            {
              name: t("global.no_partner", "No partner"),
              value: t("global.no", "No")
            }
          ]
        }
      },
      {
        accessorKey: "partnerName",
        header: t("global.created_by", "Created by")
      }
    )
  }

  const updateLocize = () => {
    const version = "version1"
    const languages = "sv,en,da,nb"
    const namespace = "latest"
    const url = apiEndpoints.attachment.updatelocizetranslations
      .replace("{version}", version)
      .replace("{languages}", languages)
      .replace("{ns}", namespace)
    console.log("url", url)
    locizeApi.get(url)
  }

  useEffect(() => {
    if (locizeResult.status === 2) {
      setShowLocizeDialog(false)
    } else if (locizeResult.status === 3 && locizeResult.error) {
      const errorData = locizeResult.error?.data
      if (errorData?.errors) {
        const errorMessages = Object.entries(errorData.errors)
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n")
        setLocizeDialogMessage(`${errorData.message || t("sysadmin.failed_update_translation", "Failed to update translations")}\n\n${errorMessages}`)
      } else if (errorData?.message) {
        setLocizeDialogMessage(errorData.message)
      } else {
        setLocizeDialogMessage(t("sysadmin.error_update_translation", "An error occurred while updating translations"))
      }
    }
  }, [locizeResult])

  return (
    <div>
      <Container>
        <Box>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h1">
              {t("settings.customer.administer_customers", "Administer customers")}
            </Typography>
            <Box display="flex" gap={2} mt={{ xs: 2 }}>
              <Button color="primary" onClick={() => setShowLocizeDialog(true)}>
                {t("global.update", "Update")} Locize
              </Button>
              <Button component={Link} color="primary" to={allRoutes.SystemadminCustomersAdd()}>
                {t("settings.customer.add_new_customer", "Add new customer")}
              </Button>
              <Button color="primary" onClick={() => setOpenUserDialog(true)}>
                {t("settings.customer.search_user", "Search user")}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Container>
      <SearchUserDialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} />
      <ContinueDialog
        title={t("sysadmin.update_translations", "Update translations")}
        message={locizeDialogMessage}
        onAddSubmit={updateLocize}
        setShowContinueModal={setShowLocizeDialog}
        result={locizeResult}
        isOpen={showLocizeDialog}
        cancelBtnText={t("global.cancel", "Cancel")}
        continueBtnText={t("global.buttons.complete", "Complete")}
      />
      <StyledTableContainer>
        <ClpTable
          rows={customersResult.status === 2 && customersResult.data}
          loadingState={customersResult.status}
          columns={columns}
          onRowClick={(row) =>
            routerHistory.push(allRoutes.SystemadminCustomersEditCustomer(row.original.id))
          }
        />
      </StyledTableContainer>
    </div>
  )
}

export default Customers
